import { createHttpClient, type NextFetchOptions } from '@outpoll/http';
import { getLocale } from 'next-intl/server';
import {
  mapNewsArticle,
  mapNewsFeed,
  mapNewsList,
  mapStrapiAuthor,
  mapStrapiCategory,
  mapStrapiNews,
  mapStrapiNewsStory,
} from './mappers';
import type {
  NewsFeed,
  NewsList,
  StrapiAuthorItem,
  StrapiCategoryItem,
  StrapiNewsItem,
  StrapiNewsStoryItem,
} from './models';
import type {
  StrapiAuthorsResponseDto,
  StrapiCategoriesResponseDto,
  StrapiNewsArticleDto,
  StrapiNewsFeedDto,
  StrapiNewsListDto,
  StrapiNewsResponseDto,
} from './types';
import { localeToStrapiLanguage, resolveStrapiBaseUrl } from './utils';

const BASE_URL = resolveStrapiBaseUrl();

const http = createHttpClient({ baseUrl: BASE_URL });

/**
 * Builds a map of parent category slugs to their subcategories for a given locale.
 *
 * Intentionally NOT cached in a module-level variable: a single shared cache
 * (even keyed by locale) was the source of section names leaking across
 * languages, and a stale process-wide map made language switches feel like they
 * served the previous locale. We fetch fresh per locale and disable the data
 * cache (`revalidate: 0`) so the active language is always reflected.
 */
async function buildSubcategoriesMap(locale: string): Promise<Map<string, StrapiCategoryItem[]>> {
  // Fetch parents (level 0) and sections (level 1) for this locale, uncached.
  const [parentCategories, sectionCategories] = await Promise.all([
    fetchStrapiCategories({ level: 0, locale, next: { revalidate: 0 } }),
    fetchStrapiCategories({ level: 1, locale, next: { revalidate: 0 } }),
  ]);

  // Build the map: parent slug -> array of sections (level 1)
  const subcategoriesMap = new Map<string, StrapiCategoryItem[]>();

  // Index parent id -> slug
  const parentIdToSlug = new Map<string, string>();
  parentCategories.forEach((p) => {
    if (p.id && p.slug) parentIdToSlug.set(p.id, p.slug);
  });

  sectionCategories.forEach((section) => {
    const parentId = section.parentCategoryId;
    const parentSlug = parentId ? parentIdToSlug.get(parentId) : undefined;
    if (parentSlug) {
      if (!subcategoriesMap.has(parentSlug)) subcategoriesMap.set(parentSlug, []);
      subcategoriesMap.get(parentSlug)!.push(section);
    }
  });

  return subcategoriesMap;
}

export async function fetchStrapiNews(locale?: string): Promise<StrapiNewsItem[]> {
  // Auto-detect language from locale
  let language: string;
  try {
    const currentLocale = locale || (await getLocale());
    language = localeToStrapiLanguage(currentLocale);
  } catch {
    language = 'en';
  }

  try {
    const dto = await http.get<StrapiNewsResponseDto>(`/api/news?size=100&language=${language}`);
    return (dto.data ?? [])
      .filter((item): item is NonNullable<typeof item> => item != null)
      .map(mapStrapiNews);
  } catch (error) {
    console.error('[strapi] fetchStrapiNews failed:', error);
    return [];
  }
}

export interface FetchNewsParams {
  page?: number; // default 1
  size?: number; // default 25
  id?: string; // exact Strapi numeric id (single)
  ids?: string[]; // batch of exact Strapi numeric ids (csv on the wire)
  slug?: string; // category slug
  category?: string; // category name
  tagLike?: string; // fuzzy tag match
  authorName?: string; // filter by author name
  tagList?: string[]; // list of tags
  getLatestTwoDays?: boolean; // get latest two days news
  weeklyRecap?: boolean; // get recap news
  fromCreatedAt?: string; // filter news from this date
  toCreatedAt?: string; // filter news to this date
  titleLike?: string; // fuzzy title match
  contentLike?: string; // fuzzy content match
  notCategory?: string; // exclude category
  tagNotLike?: string; // exclude fuzzy tag match
  notTagList?: string[]; // exclude list of tags
  conditionOperator?: 'and' | 'or'; // condition operator
  countryLike?: string; // filter by country name (fuzzy match)
  language?: string; // filter by language (if not provided, will be auto-detected from locale)
  locale?: string; // app locale (will be converted to language if language is not provided)
}

export async function fetchStrapiNewsByQuery(
  params: FetchNewsParams = {},
  options?: { next?: NextFetchOptions },
): Promise<StrapiNewsItem[]> {
  // Auto-detect language from locale if not explicitly provided
  let language: string | undefined = params.language;

  if (!language) {
    try {
      // Try to get locale from next-intl context (server component)
      const locale = params.locale || (await getLocale());
      language = localeToStrapiLanguage(locale);
    } catch {
      // If getLocale fails (e.g., in client context), fallback to 'en'
      language = 'en';
    }
  }

  try {
    const dto = await http.get<StrapiNewsResponseDto>(`/api/news`, {
      query: {
        page: params.page,
        size: params.size,
        ...(params.id ? { id: params.id } : {}),
        ...(params.ids?.length ? { ids: params.ids.join(',') } : {}),
        slug: params.slug,
        category: params.category,
        ...(params.weeklyRecap !== undefined ? { weeklyRecap: params.weeklyRecap } : {}),
        ...(params.tagLike ? { tagLike: params.tagLike } : {}),
        ...(params.authorName ? { authorName: params.authorName } : {}),
        ...(params.tagList ? { tagList: params.tagList } : {}),
        ...(params.getLatestTwoDays ? { getLatestTwoDays: params.getLatestTwoDays } : {}),
        ...(params.fromCreatedAt ? { fromCreatedAt: params.fromCreatedAt } : {}),
        ...(params.toCreatedAt ? { toCreatedAt: params.toCreatedAt } : {}),
        ...(params.titleLike ? { titleLike: params.titleLike } : {}),
        ...(params.contentLike ? { contentLike: params.contentLike } : {}),
        ...(params.notCategory ? { notCategory: params.notCategory } : {}),
        ...(params.tagNotLike ? { tagNotLike: params.tagNotLike } : {}),
        ...(params.notTagList ? { notTagList: params.notTagList } : {}),
        ...(params.conditionOperator ? { conditionOperator: params.conditionOperator } : {}),
        ...(params.countryLike ? { countryLike: params.countryLike } : {}),
        // Prefer the i18n `locale=` filter when an explicit locale is passed;
        // `language=` is kept for back-compat with callers that don't opt in yet.
        ...(params.locale ? { locale: params.locale } : {}),
        ...(language ? { language } : {}), // Include language only if it's set
      },
      next: options?.next,
    });
    return (dto.data ?? [])
      .filter((item): item is NonNullable<typeof item> => item != null)
      .map(mapStrapiNews);
  } catch (error) {
    // Don't let a transient Strapi failure abort a static export / crash a page.
    // Degrade to an empty list; ISR will repopulate on the next revalidate.
    console.error('[strapi] fetchStrapiNewsByQuery failed:', error);
    return [];
  }
}

export async function fetchStrapiRelatedNewsByCategory(
  categorySlug: string,
  maxCount: number = 12,
  locale?: string,
): Promise<StrapiNewsItem[]> {
  // Auto-detect language from locale
  let language: string;
  try {
    const currentLocale = locale || (await getLocale());
    language = localeToStrapiLanguage(currentLocale);
  } catch {
    language = 'en';
  }

  try {
    const dto = await http.get<StrapiNewsResponseDto>(`/api/news`, {
      query: {
        slug: categorySlug,
        size: maxCount,
        sort: 'createdAt:desc',
        weeklyRecap: false,
        language,
      },
    });
    return (dto.data ?? [])
      .filter((item): item is NonNullable<typeof item> => item != null)
      .map(mapStrapiNews);
  } catch (error) {
    console.error('[strapi] fetchStrapiRelatedNewsByCategory failed:', error);
    return [];
  }
}

export async function fetchStrapiCategories(options?: {
  level?: number;
  page?: number;
  size?: number;
  locale?: string;
  next?: NextFetchOptions;
}): Promise<StrapiCategoryItem[]> {
  const level = options?.level ?? 1;
  const page = options?.page ?? 1;
  const size = options?.size ?? 2000;
  // Localize category names via the backend (defaults to the active locale).
  const locale = await resolveLocale(options?.locale);

  const url = `/api/categories?page=${page}&size=${size}&level=${level}&locale=${encodeURIComponent(locale)}`;

  try {
    const dto = await http.get<StrapiCategoriesResponseDto>(url, {
      next: options?.next,
    });

    return (dto.data ?? []).map(mapStrapiCategory);
  } catch (error) {
    // Categories power the nav rendered in the layout on every page. A transient
    // Strapi outage (e.g. 404 during a build against staging) must not abort the
    // whole static export — degrade to an empty list and let ISR repopulate.
    console.error(`[strapi] fetchStrapiCategories failed for ${url}:`, error);
    return [];
  }
}

export async function fetchStrapiSectionsByCategory(
  categorySlug: string,
  locale?: string,
): Promise<StrapiCategoryItem[]> {
  // Localize section names via the backend (defaults to the active locale).
  const resolvedLocale = await resolveLocale(locale);

  // Build the subcategories map fresh for this locale
  const subcategoriesMap = await buildSubcategoriesMap(resolvedLocale);

  // Return subcategories for the given parent category slug
  return subcategoriesMap.get(categorySlug) || [];
}

export async function fetchStrapiTopStoriesByCategory(
  categorySlug: string,
  options?: { next?: NextFetchOptions; locale?: string },
): Promise<StrapiNewsStoryItem[]> {
  // Auto-detect language from locale
  let language: string;
  try {
    const currentLocale = options?.locale || (await getLocale());
    language = localeToStrapiLanguage(currentLocale);
  } catch {
    language = 'en';
  }

  try {
    const dto = await http.get<StrapiNewsResponseDto>(`/api/news`, {
      query: {
        slug: categorySlug,
        size: 5,
        language,
      },
      next: options?.next,
    });
    return (dto.data ?? []).map(mapStrapiNewsStory);
  } catch (error) {
    console.error('[strapi] fetchStrapiTopStoriesByCategory failed:', error);
    return [];
  }
}

export interface FetchAuthorsParams {
  page?: number; // default 1
  size?: number; // default 25
  id?: string;
  nameLike?: string;
}

export async function fetchStrapiAuthors(
  params: FetchAuthorsParams = {},
  options?: { next?: NextFetchOptions },
): Promise<StrapiAuthorItem[]> {
  try {
    const dto = await http.get<StrapiAuthorsResponseDto>('/api/authors', {
      query: {
        page: params.page,
        size: params.size,
        id: params.id,
        nameLike: encodeURIComponent(params.nameLike ?? ''),
      },
      next: options?.next,
    });
    return (dto.data ?? []).map(mapStrapiAuthor);
  } catch (error) {
    console.error('[strapi] fetchStrapiAuthors failed:', error);
    return [];
  }
}

// ---------------------------------------------------------------------------
// News v2 (i18n) endpoints — aggregated feed, paginated list, single article.
// These take the app `locale` directly (en/es/pt/zh/ja/ko/de/id) via `locale=`.
// ---------------------------------------------------------------------------

async function resolveLocale(locale?: string): Promise<string> {
  if (locale) return locale;
  try {
    return await getLocale();
  } catch {
    return 'en';
  }
}

/**
 * Aggregated home feed — replaces the 5 parallel `/api/news` calls.
 * Returns named rails (hottest / editorialPicks / featured / latest / other);
 * deduplication across rails is handled server-side.
 */
export async function fetchNewsFeed(
  params: { locale?: string; page?: 'home' } = {},
  options?: { next?: NextFetchOptions },
): Promise<NewsFeed> {
  const locale = await resolveLocale(params.locale);
  const dto = await http.get<StrapiNewsFeedDto>('/api/news/feed', {
    query: { page: params.page ?? 'home', locale },
    next: options?.next,
  });
  return mapNewsFeed(dto);
}

export interface FetchNewsListParams {
  locale?: string;
  page?: number;
  size?: number;
  category?: string;
  exclude?: string[]; // tags to exclude (replaces legacy notTagList)
}

/**
 * Paginated list for "Load more" — replaces page-2+ `/api/news` calls.
 */
export async function fetchNewsList(
  params: FetchNewsListParams = {},
  options?: { next?: NextFetchOptions },
): Promise<NewsList> {
  const locale = await resolveLocale(params.locale);
  const dto = await http.get<StrapiNewsListDto>('/api/news/list', {
    query: {
      locale,
      page: params.page,
      size: params.size,
      ...(params.category ? { category: params.category } : {}),
      ...(params.exclude?.length ? { exclude: params.exclude.join(',') } : {}),
    },
    next: options?.next,
  });
  return mapNewsList(dto);
}

/**
 * Single article by localized slug. Carries full content plus i18n metadata
 * (availableLocales, isFallback, requestedLocale) for hreflang / canonical.
 * Returns null when the slug doesn't resolve.
 */
export async function fetchNewsArticle(
  slug: string,
  params: { locale?: string } = {},
  options?: { next?: NextFetchOptions },
): Promise<StrapiNewsItem | null> {
  const locale = await resolveLocale(params.locale);
  const dto = await http.get<StrapiNewsArticleDto | null>(
    `/api/news/article/${encodeURIComponent(slug)}`,
    {
      query: { locale },
      next: options?.next,
    },
  );
  if (!dto) return null;
  return mapNewsArticle(dto);
}
